/**
 * All ModuleCertificate CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.moduleCertificate;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_MODULE_CERTIFICATE = "INVALIDATE_SELECTED_MODULE_CERTIFICATE"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_MODULE_CERTIFICATE
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleModuleCertificateById(id))
  } else {
    return dispatch(returnSingleModuleCertificatePromise(id)); // return promise that contains moduleCertificate
  }
}

export const returnSingleModuleCertificatePromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_MODULE_CERTIFICATE_WITHOUT_FETCHING"
      , id: id
      , item: getState().moduleCertificate.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_MODULE_CERTIFICATE = "REQUEST_SINGLE_MODULE_CERTIFICATE";
function requestSingleModuleCertificate(id) {
  return {
    type: REQUEST_SINGLE_MODULE_CERTIFICATE
    , id
  }
}

export const RECEIVE_SINGLE_MODULE_CERTIFICATE = "RECEIVE_SINGLE_MODULE_CERTIFICATE";
function receiveSingleModuleCertificate(json) {
  return {
    type: RECEIVE_SINGLE_MODULE_CERTIFICATE
    , id: json.moduleCertificate ? json.moduleCertificate._id : null
    , item: json.moduleCertificate
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleModuleCertificateById(moduleCertificateId) {
  return dispatch => {
    dispatch(requestSingleModuleCertificate(moduleCertificateId))
    return callAPI(`/api/module-certificates/${moduleCertificateId}`)
      .then(json => dispatch(receiveSingleModuleCertificate(json)))
  }
}

export function sendModuleCertificateAttestationEmail(id) {
  return dispatch => {
    return callAPI(`/api/module-certificates/${id}/resend-attestation`, 'POST', {})
    .then(json => json)
  }
}

export function sendModuleCertificateAttestation(id, attestationHex) {
  return dispatch => {
    return callAPI(`/api/module-certificates/${id}/complete-attestation`, 'POST', {
      attestationHex
    })
    .then(json => json)
  }
}


export function fetchModuleRedirectStrategies(id) {
  return dispatch => {
    return callAPI(`/api/module-certificates/${id}/redirect-strategies`)
    .then(json => json)
  }
}

export const ADD_SINGLE_MODULE_CERTIFICATE_TO_MAP = "ADD_SINGLE_MODULE_CERTIFICATE_TO_MAP";
export function addSingleModuleCertificateToMap(item) {
  return {
    type: ADD_SINGLE_MODULE_CERTIFICATE_TO_MAP
    , item
  }
}

export const REQUEST_CREATE_MODULE_CERTIFICATE = "REQUEST_CREATE_MODULE_CERTIFICATE";
function requestCreateModuleCertificate(moduleCertificate) {
  return {
    type: REQUEST_CREATE_MODULE_CERTIFICATE
    , moduleCertificate
  }
}

export const RECEIVE_CREATE_MODULE_CERTIFICATE = "RECEIVE_CREATE_MODULE_CERTIFICATE";
function receiveCreateModuleCertificate(json) {
  return {
    type: RECEIVE_CREATE_MODULE_CERTIFICATE
    , id: json.moduleCertificate ? json.moduleCertificate._id : null
    , item: json.moduleCertificate
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateModuleCertificate(data) {
  return dispatch => {
    dispatch(requestCreateModuleCertificate(data))
    return callAPI('/api/module-certificates', 'POST', data)
      .then(json => dispatch(receiveCreateModuleCertificate(json)))
  }
}

export const REQUEST_UPDATE_MODULE_CERTIFICATE = "REQUEST_UPDATE_MODULE_CERTIFICATE";
function requestUpdateModuleCertificate(moduleCertificate) {
  return {
    id: moduleCertificate ? moduleCertificate._id : null
    , moduleCertificate
    , type: REQUEST_UPDATE_MODULE_CERTIFICATE
  }
}

export const RECEIVE_UPDATE_MODULE_CERTIFICATE = "RECEIVE_UPDATE_MODULE_CERTIFICATE";
function receiveUpdateModuleCertificate(json) {
  return {
    type: RECEIVE_UPDATE_MODULE_CERTIFICATE
    , id: json.moduleCertificate ? json.moduleCertificate._id : null
    , item: json.moduleCertificate
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateModuleCertificate(data) {
  return dispatch => {
    dispatch(requestUpdateModuleCertificate(data))
    return callAPI(`/api/module-certificates/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateModuleCertificate(json)))
  }
}

export const REQUEST_DELETE_MODULE_CERTIFICATE = "REQUEST_DELETE_MODULE_CERTIFICATE";
function requestDeleteModuleCertificate(id) {
  return {
    type: REQUEST_DELETE_MODULE_CERTIFICATE
    , id
  }
}

export const RECEIVE_DELETE_MODULE_CERTIFICATE = "RECEIVE_DELETE_MODULE_CERTIFICATE";
function receiveDeleteModuleCertificate(id, json) {
  return {
    id
    , error: json.message
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_DELETE_MODULE_CERTIFICATE
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteModuleCertificate(id))
    return callAPI(`/api/module-certificates/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteModuleCertificate(id, json)))
  }
}


/**
 * MODULE_CERTIFICATE LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest moduleCertificateLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.moduleCertificate.lists, {});
  let list = { ...state.moduleCertificate.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnModuleCertificateListPromise(...listArgs));
  }
}

export const returnModuleCertificateListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.moduleCertificate.byId[id]);

  return new Promise((resolve) => {
    resolve({
      list: listItems
      , listArgs: listArgs
      , success: true
      , type: "RETURN_MODULE_CERTIFICATE_LIST_WITHOUT_FETCHING"
    })
  });
}

export const REQUEST_MODULE_CERTIFICATE_LIST = "REQUEST_MODULE_CERTIFICATE_LIST"
function requestModuleCertificateList(listArgs) {
  return {
    type: REQUEST_MODULE_CERTIFICATE_LIST
    , listArgs
  }
}

export const RECEIVE_MODULE_CERTIFICATE_LIST = "RECEIVE_MODULE_CERTIFICATE_LIST"
function receiveModuleCertificateList(json, listArgs) {
  return {
    type: RECEIVE_MODULE_CERTIFICATE_LIST
    , listArgs
    , list: json.moduleCertificates
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_MODULE_CERTIFICATE_TO_LIST = "ADD_MODULE_CERTIFICATE_TO_LIST";
export function addModuleCertificateToList(id, ...listArgs) {
  // console.log("Add moduleCertificate to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_MODULE_CERTIFICATE_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_MODULE_CERTIFICATE_FROM_LIST = "REMOVE_MODULE_CERTIFICATE_FROM_LIST"
export function removeModuleCertificateFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_MODULE_CERTIFICATE_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    dispatch(requestModuleCertificateList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/module-certificates/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/module-certificates/by-[ARG1]/[ARG2]".
     *    ex: /api/module-certificates/by-category/:category
     *  if 2nd arg is an array, though, return "/api/module-certificates/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/module-certificates";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of moduleCertificates given an array of moduleCertificate id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveModuleCertificateList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_MODULE_CERTIFICATE_FILTER = "SET_MODULE_CERTIFICATE_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_MODULE_CERTIFICATE_FILTER
    , filter
    , listArgs
  }
}

export const SET_MODULE_CERTIFICATE_PAGINATION = "SET_MODULE_CERTIFICATE_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_MODULE_CERTIFICATE_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_MODULE_CERTIFICATE_LIST = "INVALIDATE_MODULE_CERTIFICATE_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: INVALIDATE_MODULE_CERTIFICATE_LIST
    , listArgs
  }
}



// WEBPACK FOOTER //
// modules/moduleCertificate/moduleCertificateActions.js